import React, { useContext } from "react";
import {
    FlexContainer,
    CardHeadingLabel,
    ExplainParagraphLabel,
    DocumentUploader,
    Toast
} from "src/components";
import { UserContext } from "src/scenes/App/UserContext";
import { UserKeys } from "src/constants/userDetails";
import { MODE_SOURCE } from "src/components/Documents/utils/documentHelpers";
import { uploadDocuments } from "src/components/Documents/api/documentsApi";
import EmployeeDocumentUploader from "src/scenes/EmployeeList/components/Documents/EmployeeDocumentUploader";
import { EMPLOYEE_MODE_SOURCE } from "src/scenes/EmployeeList/components/Documents/utils";

import {
    SubText, Section, SectionHeader,
    SectionContent, TierCard,
    TierCardContent, LicenseTextGroup,
    LicenseTitle, CancelButton,
    LicenseDetails, SaveButton,
    Icon, ActionButtonsWrapper
} from "./MyLicense.style";

import CheckIcon from "src/img/Green-Check.svg";

function MyLicense() {
    const userContext = useContext(UserContext);
    const companyName = userContext?.[UserKeys.COMPANY_NAME];
    const licenseTier = userContext?.[UserKeys.LICENSE_TIER];
    const isClient = userContext?.[UserKeys.IS_CLIENT];

    const requiredDocuments = React.useMemo(
        () => isClient
            ? [
                ...(licenseTier?.requiredDocuments ?? []),
                ...(userContext?.[UserKeys.REQUIRED_DOCUMENTS] ?? [])
            ]
            : (userContext?.[UserKeys.REQUIRED_DOCUMENTS] ?? []),
        [
            isClient,
            licenseTier?.requiredDocuments,
            userContext?.[UserKeys.REQUIRED_DOCUMENTS]
        ]
    );

    const isYearly = licenseTier?.renewalPeriod === "upfront";
    const durationLabel = isYearly ? "Yearly" : "Monthly";

    const [docState, setDocState] = React.useState({
        documents: [],
        selected: []
    });

    const [loading, setLoading] = React.useState(false);
    
    // to directly load the docs uploaded from modal without page refresh
    React.useEffect(() => {
        userContext?.appFns?.reinitialiseUser?.();
    }, []);

    const handleSave = async () => {
        try {
            setLoading(true);
            const filesToUpload = docState?.documents?.map(doc =>
                doc instanceof File ? doc : null
            );

            if (!filesToUpload || !filesToUpload.some(Boolean)) {
                console.warn("No documents to upload");
                return;
            }

            await uploadDocuments({
                requiredDocuments,
                documents: filesToUpload
            });

            Toast.success("Upload successful");
            setDocState({
                documents: [],
                selected: []
            });

            userContext?.appFns?.reinitialiseUser?.();

        } catch (err) {
            console.log("Upload failed:", err);
            Toast.error(err.getApiErrorDetails().message);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setDocState({
            documents: [],
            selected: []
        });
    };

    const hasUploads = isClient
        ? docState?.selected?.length > 0
        : docState?.documents?.some(doc => doc instanceof File);

    return (
        <FlexContainer height="100%">
            <FlexContainer marginBottom="2em">

                <CardHeadingLabel>{licenseTier ? "My License" : "My Documents"}</CardHeadingLabel>

                <ExplainParagraphLabel style={{ marginTop: "-5px" }}>
                    Manage your license and supporting documents in one place. View your active <br/>
                    license details, track its validity, and access or manage all required documents <br/>
                    associated with your subscription.
                </ExplainParagraphLabel>

                {/* LICENSE SECTION */}
                {licenseTier && (
                    <Section marginTop="20px">
                        <SectionHeader>
                            <SubText style={{ marginBottom: "10px" }} weight={700}>Selected License</SubText>

                            <SubText size="14px" weight={500}>
                                This is the license currently active on your {companyName} subscription.
                            </SubText>
                        </SectionHeader>

                        <SectionContent>
                            <TierCard>
                                <TierCardContent>
                                    <LicenseTextGroup>
                                        <LicenseTitle>
                                            {licenseTier.tierName} License
                                        </LicenseTitle>
                                        { licenseTier.freeForever &&
                                            <>
                                                <LicenseDetails>
                                                    No expiry
                                                </LicenseDetails>
                                            </>
                                        }
                                        { !licenseTier.freeForever &&
                                            <>
                                                <LicenseDetails>
                                                    AUD ${licenseTier.renewalCost} ({durationLabel})
                                                </LicenseDetails>

                                                <LicenseDetails>
                                                    {`Valid until ${licenseTier.validUntil}`}
                                                </LicenseDetails>
                                            </>
                                        }
                                    </LicenseTextGroup>

                                    <Icon src={CheckIcon} />

                                </TierCardContent>
                            </TierCard>
                        </SectionContent>
                    </Section>
                )}

                {/* DOCUMENT SECTION */}
                <Section marginTop="50px">
                    <SectionHeader>
                        <SubText weight={700} style={{ marginBottom: "10px" }}>Manage Uploaded Documents</SubText>

                        <SubText size="14px">
                            View and manage all documents requested by your company.
                        </SubText>
                    </SectionHeader>
                </Section>

                <Section>
                    {isClient ? (
                        <DocumentUploader
                            requiredDocuments={requiredDocuments}
                            title="Documents Uploaded"
                            mode={MODE_SOURCE.SETTINGS}
                            onChange={(data) => setDocState(data)}
                        />
                    ) : (
                        <EmployeeDocumentUploader
                            requiredDocuments={requiredDocuments}
                            title="Documents Uploaded"
                            mode={EMPLOYEE_MODE_SOURCE.SETTINGS}
                            onChange={(data) => setDocState(data)}
                            refresh={() => userContext?.appFns?.reinitialiseUser?.()}
                        />
                    )}
                </Section>

                {hasUploads && (
                    <ActionButtonsWrapper>
                        <CancelButton onClick={handleCancel}>
                            Cancel
                        </CancelButton>

                        <SaveButton onClick={handleSave} disabled={loading}>
                            {loading ? "Saving..." : "Save Documents"}
                        </SaveButton>
                    </ActionButtonsWrapper>
                )}

            </FlexContainer>
        </FlexContainer>
    );
}

export default MyLicense;
